/* eslint-disable @typescript-eslint/naming-convention */
import { getLocaleStore } from '@/i18n';
import type { ServiceManager } from '../service-manager';
import { KeyboardHotkey } from './keyboard';
import { HotkeyType } from './type';

/** 复制/剪切/粘贴快捷键 */
export class ClipboardHotkey extends KeyboardHotkey {
    /** 优先使用系统复制/剪切/粘贴的元素 */
    // private __EXCLUDE_ELEMENT_NAMES = ['INPUT', 'TEXTAREA'];
    /** 禁止粘贴的class */
    // private __EXCLUDE_CLASS_NAMES = ['ant-scrolling-effect'];

    private __pasteEventName = 'system:paste';
    private __copyEventName = 'system:copy';
    private __cutEventName = 'system:cut';

    constructor(readonly service: ServiceManager) {
        super(service);
    }

    mount() {
        const { $t } = getLocaleStore();
        super.keyboardMount();
        super.register(
            ...[
                {
                    key: HotkeyType.CUT,
                    name: $t((v) => v.Text_Cut),
                    win: 'Ctrl X',
                    mac: '⌘ X',
                },
                {
                    key: HotkeyType.COPY,
                    name: $t((v) => v.Text_Copy),
                    win: 'Ctrl C',
                    mac: '⌘ C',
                },
                {
                    key: HotkeyType.PASTE,
                    name: $t((v) => v.Text_Paste),
                    win: 'Ctrl V',
                    mac: '⌘ V',
                },
            ],
        );
        document.addEventListener('paste', this.__pasteHandle);
        document.addEventListener('copy', this.__copyHandle);
        document.addEventListener('cut', this.__cutHandle);
    }

    unmount() {
        super.keyboardUnmount();
        document.removeEventListener('paste', this.__pasteHandle);
        document.removeEventListener('copy', this.__copyHandle);
        document.removeEventListener('cut', this.__cutHandle);
    }

    onPaste(fn: (e: ClipboardEvent) => void, weight = 0) {
        return this.service.events.on(this.__pasteEventName, fn, weight);
    }

    onCopy(fn: (e: ClipboardEvent) => void, weight = 0) {
        return this.service.events.on(this.__copyEventName, fn, weight);
    }

    onCut(fn: (e: ClipboardEvent) => void, weight = 0) {
        return this.service.events.on(this.__cutEventName, fn, weight);
    }

    private __pasteHandle = (e: ClipboardEvent) => {
        if (this.__checkTargetSystemOperate(e)) return;
        if (this._checkDisablePressEvent()) return;
        this.service.events.emit(this.__pasteEventName, e);
    };

    private __copyHandle = (e: ClipboardEvent) => {
        if (this.__checkTargetSystemOperate(e)) return;
        if (this._checkDisablePressEvent()) return;
        this.service.events.emit(this.__copyEventName, e);
    };

    private __cutHandle = (e: ClipboardEvent) => {
        if (this.__checkTargetSystemOperate(e)) return;
        if (this._checkDisablePressEvent()) return;
        this.service.events.emit(this.__cutEventName, e);
    };

    private __checkTargetSystemOperate(e: Event) {
        const target = e?.target as HTMLElement | null;
        if (!target) return false;

        if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
            if (target.readOnly) return false;
            return true;
        }

        // 普通元素直接使用 isContentEditable 属性
        if (target?.isContentEditable) return true;

        // 全选剪切会导致 target 脱离 dom 树 isContentEditable 丢失，需使用事件传播经过路径来查找 contentEditable 属性
        const attr = 'contenteditable';
        const contentEditableElement = e
            .composedPath()
            .find((node): node is HTMLElement => node instanceof HTMLElement && node.hasAttribute(attr));

        return contentEditableElement?.getAttribute(attr) === 'true';
    }
}
